import React from 'react';
import { Formik } from 'formik';
import type { FormikConfig } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Yup from 'yup';
import { isAfter, isValid } from 'date-fns';

import { DialogContentText, FormLabel } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';

import { TranslationKeys } from 'app/translations';

import { ActionButton, FormikModal } from 'app/components';
import { useLocale } from 'app/components/Intl';

export const BLOCK_USER_DIALOG_DATA_TEST_ID = 'block-user-dialog';

export type BlockUserFormValues = {
  blockUntil: Date | null;
};

type BlockUserDialogProps = {
  show: boolean;
  onSubmit: FormikConfig<BlockUserFormValues>['onSubmit'];
  onClose: () => void;
};

export const BlockUserDialog = ({ show, onSubmit, onClose }: BlockUserDialogProps) => {
  const intl = useIntl();
  const { locale } = useLocale();

  const formikInitialValues = React.useMemo<BlockUserFormValues>(() => ({ blockUntil: null }), []);

  const formikValidationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        blockUntil: Yup.mixed()
          .nullable()
          .test(
            'isFutureDateTime',
            intl.formatMessage({ id: TranslationKeys.users_block_dateMustBeInFuture }),
            value => !value || (isValid(value) && isAfter(value as Date, new Date())),
          ),
      }),
    [intl],
  );

  return (
    <Formik<BlockUserFormValues>
      initialValues={formikInitialValues}
      validationSchema={formikValidationSchema}
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, submitForm, isSubmitting, isValid, resetForm, setFieldValue, setFieldTouched }) => {
        const closeDialog = () => {
          onClose();
          resetForm();
        };

        const showError = !!touched.blockUntil && !!errors.blockUntil;

        return (
          <FormikModal
            show={show}
            onHide={closeDialog}
            title={<FormattedMessage id={TranslationKeys.users_block_confirmationTitle} />}
            testId={BLOCK_USER_DIALOG_DATA_TEST_ID}
            actions={
              <>
                <ActionButton
                  messageId={TranslationKeys.button_cancel}
                  onClick={closeDialog}
                  variant="outlined"
                  disabled={isSubmitting}
                />
                <ActionButton
                  messageId={TranslationKeys.users_block}
                  onClick={submitForm}
                  disabled={isSubmitting || !isValid}
                />
              </>
            }
          >
            <DialogContentText>
              <FormattedMessage id={TranslationKeys.users_block_confirmationMessage} />
            </DialogContentText>
            <FormLabel>
              <FormattedMessage id={TranslationKeys.users_block_blockedUntil} />
            </FormLabel>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
              <DateTimePicker
                value={values.blockUntil}
                disablePast
                closeOnSelect={false}
                // The desktop variant only allows typing the time by default; render a time selector instead.
                viewRenderers={{
                  hours: renderTimeViewClock,
                  minutes: renderTimeViewClock,
                }}
                onChange={value => {
                  // Do not switch this order, see https://github.com/formium/formik/issues/2083#issuecomment-884831583
                  setFieldTouched('blockUntil', true, false);
                  setFieldValue('blockUntil', value, true);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: showError,
                    helperText: showError ? errors.blockUntil : undefined,
                  },
                  tabs: { hidden: false },
                  actionBar: { actions: ['clear', 'accept'] },
                }}
              />
            </LocalizationProvider>
          </FormikModal>
        );
      }}
    </Formik>
  );
};
